import { Injectable, inject } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';
import CryptoJS from 'crypto-js';

/**
 * BUG-274522 — Aquí vivían una clave AES y un IV fijos, escritos en el propio código que se
 * envía al navegador (y repetidos, idénticos, en la app móvil y en el otro frontend). La
 * opción `encrypted: true` que ofrecían daba apariencia de cifrado en reposo sin protección
 * real: quien descargue el bundle obtiene la clave en minutos, y el IV fijo reutilizado es
 * además un anti-patrón de AES-CBC.
 *
 * En el navegador no existe un equivalente al Keychain/Keystore que sí usa la app móvil, así
 * que no hay dónde guardar bien la clave. En vez de dejar un cifrado decorativo, la opción se
 * elimina: `encrypted: true` ya no se acepta. Ningún llamador de producción la usaba.
 * Para datos que de verdad deban protegerse, no persistirlos en el cliente.
 */
import { AppLoggerService } from './app-logger.service';

/** SHA-256 del nombre de la key, para que su nombre no sea observable en el almacén. */
function hash(value: string): string {
  return CryptoJS.SHA256(value).toString();
}

interface StorageOptions {
  /** Si es `true`, la key se hashea con SHA-256 antes de persistir. */
  cypherKey?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private dbName = 'fnaCitizen';
  private dbVersion = 4;
  private dbPromise: Promise<IDBPDatabase<unknown>>;
  private logger = inject(AppLoggerService);

  constructor() {
    this.dbPromise = openDB(this.dbName, this.dbVersion, {
      upgrade(db) {
        const storeNames = [
          'general',
          'endUserData',
          'sessionData',
          'configuration',
          'txnCache',
          'sessionData',
          'devices',
          'forms',
          'maintenance',
        ];
        storeNames.forEach(storeName => {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName);
          }
        });
      },
    });
  }

  async getItem<T = unknown>(storeName: string, key: string, options: StorageOptions = {}): Promise<T | null> {
    const db = await this.dbPromise;
    const { cypherKey = false } = options;
    const newKey = cypherKey ? hash(key) : key;
    let itemValue: string | null = null;
    let value: string | null = null;

    const localStorageValue = localStorage.getItem(newKey);
    let indexDBValue: string | null = null;
    if (db.objectStoreNames.contains(storeName)) {
      indexDBValue = await db.get(storeName, newKey);
    }
    value = indexDBValue ?? localStorageValue;
    if (indexDBValue && localStorageValue && indexDBValue !== localStorageValue) {
      this.logger.warn(`${key} es diferente entre almacenamientos`);
    }
    itemValue = value;
    itemValue = itemValue ? JSON.parse(itemValue) : null;
    if (!indexDBValue && value) {
      await this.setItem(storeName, key, itemValue, options);
    }
    if (localStorageValue) {
      localStorage.removeItem(newKey);
    }
    return itemValue as T;
  }

  async setItem(storeName: string, key: string, value: unknown, options: StorageOptions = {}) {
    const db = await this.dbPromise;
    const { cypherKey = false } = options;
    const newKey = cypherKey ? hash(key) : key;
    if (value === null) {
      return this.removeItem(storeName, key);
    }
    const valueToStore = JSON.stringify(value);
    await db.put(storeName, valueToStore, newKey);
  }

  async removeItem(storeName: string, key: string, options: StorageOptions = {}) {
    const db = await this.dbPromise;
    const { cypherKey = false } = options;
    const newKey = cypherKey ? hash(key) : key;
    const tx = db.transaction(storeName, 'readwrite');
    await tx.store.delete(newKey);
    await tx.done;
  }
}
